import { useNavigate } from 'react-router-dom';
import { EventConfigurationErrorCode, ProjectType } from 'nekst-api';
import { EmailSetupPage } from "./email/UnifiedEmailPage";

export default function useSettingsNavigator() {
  const navigate = useNavigate();
  const getCustomRolesLink = () => '/settings/roles';

  const getProfileLink = () => '/settings/profile';

  const getUpdatePasswordPageLink = () => '/settings/password';

  const getBillingPageLink = () => '/settings/billing';

  const getProjectDetailsLink = (projectType: ProjectType) => `/settings/detailboxes/${projectType.toLowerCase()}`;

  const openUpdateProfilePage = () => navigate(getProfileLink());

  const openUpdatePasswordPage = () => navigate(getUpdatePasswordPageLink());

  const openBillingPage = () => navigate(getBillingPageLink());

  const openCustomRolesList = () => navigate(getCustomRolesLink());

  const isBillingPage = () => window.location.pathname.startsWith(getBillingPageLink());

  const openProjectDetailsFieldPage = (projectType: ProjectType) => navigate(
    getProjectDetailsLink(projectType)
  );

  const getDetailsSetLink = (projectType: ProjectType, setId: number) => `/settings/detailboxes/${projectType.toLowerCase()}/${setId}`;

  const openDetailsSetPage = (projectType: ProjectType, setId: number) => navigate(getDetailsSetLink(projectType, setId));

  const getClientPortalSettingsLink = () => '/settings/clientportal';

  const openClientPortalSettingsPage = () => navigate(getClientPortalSettingsLink());

  const getServiceProvidersLink = () => '/settings/serviceproviders';

  const openServiceProvidersPage = () => navigate(getServiceProvidersLink());

  const getImpersonateUserLink = () => '/admin/users';

  const openImpersonateUserLinkPage = () => navigate(getImpersonateUserLink());

  const getDocumentLabelsLink = () => '/settings/documentlabels';

  const openDocumentLabelsPage = () => navigate(getDocumentLabelsLink());

  const getDocumentsTemplatesLink = () => '/settings/documentstemplates';

  const openDocumentsTemplatesPage = () => navigate(getDocumentsTemplatesLink());

  const getNotificationsSettingsLink = () => '/settings/notifications';

  const openNotificationsSettingsPage = () => navigate(getNotificationsSettingsLink());

  const getDefaultPortalConfigurationLink = (projectType: ProjectType) => `/settings/clientportal/${projectType.toLowerCase()}/default`;

  const getMembersManagementLink = () => '/settings/members';

  const openMembersManagementPage = () => navigate(getMembersManagementLink());

  const openUpdateMemberPage = (id: number) => navigate(`${getMembersManagementLink()}/${id}`);

  const getTasksGroupsManagementLink = () => '/settings/tasksgroups';

  const openTasksGroupsManagementPage = () => navigate(getTasksGroupsManagementLink());

  const getAiTrainingLink = () => '/settings/aitraining';

  const openAiTrainingPage = () => navigate(getAiTrainingLink());

  const getAiTemplateManagingLink = (templateId: number) => `/settings/aitraining/${templateId}`;

  const openAiTemplateManagingPage = (templateId: number) => navigate(getAiTemplateManagingLink(templateId));

  const getSendingEmailPageLink = () => '/settings/sendingemail';

  const openEmailConfigurationPage = () => navigate('/settings/emailconfiguration');

  const getEmailSetupLink = (page?: EmailSetupPage) => page ? '/settings/email/' + page : '/settings/email';

  const openEmailSetupPage = (page?: EmailSetupPage) => navigate(getEmailSetupLink(page));

  const openSendingEmailPage = () => navigate(getSendingEmailPageLink());

  const openWebhooksPage = () => navigate('/settings/webhooks');

  const openWebhookRequestsLogPage = (subscriptionId: number) => navigate(`/settings/webhooks/${subscriptionId}`);

  const getCalendarIntegrationPageLink = (errorCode?: EventConfigurationErrorCode) => {
    let result = '/settings/calendarintegration';

    if (errorCode) {
      result += `?errorCode=${errorCode}`;
    }

    return result;
  };

  const openCalendarIntegrationPage = (
    errorCode?: EventConfigurationErrorCode
  ) => navigate(getCalendarIntegrationPageLink(errorCode));

  const getFubProfilesPageLink = (projectType?: ProjectType) => {
    const query = projectType ? `?type=${projectType}` : '';
    return '/settings/integrations/fub' + query;
  };

  const openFubProfilesPage = (projectType?: ProjectType) => {
    navigate(getFubProfilesPageLink(projectType));
  };

  const getMarketsLink = (
    openForm = false,
  ) => '/settings/markets' + (openForm ? '?openForm=1' : '');

  const openMarketsPage = (
    openForm = false,
  ) => navigate(getMarketsLink(openForm));

  const getMarketRequestsLink = (openForm: boolean) => `/settings/markets/requests${openForm ? '?openForm=1' : ''}`;

  const openMarketRequestsPage = (openForm: boolean) => navigate(getMarketRequestsLink(openForm));

  const getMarketDetailBoxesPageLink = (setupAreaId: number | string) => `/settings/markets/${setupAreaId}/detailboxes`;

  const openMarketDetailBoxesPage = (setupAreaId: number | string, projectType?: ProjectType) => {
    navigate(getMarketDetailBoxesPageLink(setupAreaId) + '/' + (projectType ? projectType.toLowerCase() : ''));
  };

  const openCustomProcessDetailFieldsPage = (setupAreaId: string | number) => {
    return navigate(`/settings/customprocesses/${setupAreaId}`);
  };

  const getAdminMarketsLink = () => '/admin/setupareas';

  const openAdminMarketsPage = () => navigate(getAdminMarketsLink());

  const getAdminStateMarketsLink = (state: string | undefined) => `/admin/setupareas/${state || 'nostate'}`;

  const openAdminStateMarketsPage = (state: string | undefined) => {
    navigate(getAdminStateMarketsLink(state));
  };


  const getDetailsFieldGroupsLink = () => '/settings/detailsgroups';

  const openDetailsFieldGroupsPage = () => navigate(getDetailsFieldGroupsLink());

  const getAdminMarketDetailsFieldsLink = (state: string | undefined, setupAreaId: number, setupAreaVersion: string) => `/admin/setupareas/${state || 'nostate'}/${setupAreaId}/${encodeURIComponent(setupAreaVersion)}/detailboxes`;

  const openAdminMarketDetailsFieldsPage = (state: string | undefined, setupAreaId: number, setupAreaVersion: string) => {
    navigate(getAdminMarketDetailsFieldsLink(state, setupAreaId, setupAreaVersion));
  };

  const getAdminSetupAreaPageLink = (state: string | undefined, setupAreaId: number) => `/admin/setupareas/${state || 'nostate'}/${setupAreaId}`;

  const openAdminSetupAreaPage = (state: string | undefined, setupAreaId: number) => {
    navigate(getAdminSetupAreaPageLink(state, setupAreaId));
  }

  const getAdminSetupAreaVersionPageLink = (state: string | undefined, setupAreaId: number, setupAreaVersion: string) => `/admin/setupareas/${state || 'nostate'}/${setupAreaId}/${encodeURIComponent(setupAreaVersion)}`;

  const openAdminSetupAreaVersionPage = (state: string | undefined, setupAreaId: number, setupAreaVersion: string) => {
    navigate(getAdminSetupAreaVersionPageLink(state, setupAreaId, setupAreaVersion));
  };

  const getSetupAreaPlansPageLink = (state: string | undefined, setupAreaId: number, setupAreaVersion: string) => `/admin/setupareas/${state || 'nostate'}/${setupAreaId}/${encodeURIComponent(setupAreaVersion)}/workflows`;

  const openAdminSetupAreaPlansPage = (state: string | undefined, setupAreaId: number, setupAreaVersion: string) => {
    navigate(getSetupAreaPlansPageLink(state, setupAreaId, setupAreaVersion));
  };

  const getAdminMarketAiTrainingLink = (state: string | undefined, setupAreaId: number, setupAreaVersion: string) => `/admin/setupareas/${state || 'nostate'}/${setupAreaId}/${encodeURIComponent(setupAreaVersion)}/aitraining`;

  const openAdminMarketAiTrainingPage = (state: string | undefined, setupAreaId: number, setupAreaVersion: string) => {
    return navigate(getAdminMarketAiTrainingLink(state, setupAreaId, setupAreaVersion));
  };

  const getAdminMarketPlanLink = (
    state: string,
    setupAreaId: number,
    setupAreaVersion: string,
    planId: number
  ) => `/admin/setupareas/${state}/${setupAreaId}/${encodeURIComponent(setupAreaVersion)}/workflows/${planId}`;

  const openAdminMarketPlanPage = (
    state: string,
    setupAreaId: number,
    setupAreaVersion: string,
    planId: number
  ) => {
    navigate(getAdminMarketPlanLink(state, setupAreaId, setupAreaVersion, planId));
  };

  const getAdminTasksGroupsManagementLink = () => '/admin/tasksgroups';

  const openAdminTasksGroupsManagementPage = () => navigate(getAdminTasksGroupsManagementLink());

  const getNewClientPortalConfigurationLink = () => '/settings/clientportal/new';

  const openNewClientPortalConfigurationPage = (setupAreaId: string, projectType: ProjectType) => {
    if (setupAreaId && projectType) {
      navigate(getNewClientPortalConfigurationLink() + `/${setupAreaId}/${projectType.toLowerCase()}`);
    } else {
      navigate(getNewClientPortalConfigurationLink());
    }
  };

  const getAdminMarketRequestsLink = () => '/admin/marketsrequests';

  const openAdminMarketRequestsPage = () => navigate(getAdminMarketRequestsLink());

  const getAdminMarketRequestLink = (id: number) => `/admin/marketsrequests/${id}`;

  const openAdminMarketRequestPage = (id: number) => navigate(getAdminMarketRequestLink(id));

  const getNewEmailSettingsLink = () => '/settings/email';

  const openNewEmailSettingsPage = () => navigate(getNewEmailSettingsLink());

  return {
    getProfileLink,
    getUpdatePasswordPageLink,
    getCustomRolesLink,
    getBillingPageLink,
    getProjectDetailsLink,
    openUpdateProfilePage,
    openUpdatePasswordPage,
    openBillingPage,
    openCustomRolesList,
    openProjectDetailsFieldPage,
    isBillingPage,
    getClientPortalSettingsLink,
    openClientPortalSettingsPage,
    getServiceProvidersLink,
    openServiceProvidersPage,
    getImpersonateUserLink,
    openImpersonateUserLinkPage,
    getDocumentLabelsLink,
    openDocumentLabelsPage,
    getDocumentsTemplatesLink,
    openDocumentsTemplatesPage,
    getDefaultPortalConfigurationLink,
    getNotificationsSettingsLink,
    openNotificationsSettingsPage,
    getMembersManagementLink,
    openMembersManagementPage,
    openUpdateMemberPage,
    getTasksGroupsManagementLink,
    openTasksGroupsManagementPage,
    getAiTrainingLink,
    openAiTrainingPage,
    getAiTemplateManagingLink,
    openAiTemplateManagingPage,
    getDetailsSetLink,
    openDetailsSetPage,
    getSendingEmailPageLink,
    openEmailConfigurationPage,
    openSendingEmailPage,
    getCalendarIntegrationPageLink,
    openCalendarIntegrationPage,
    openWebhooksPage,
    openWebhookRequestsLogPage,
    getFubProfilesPageLink,
    openFubProfilesPage,
    getAdminMarketsLink,
    openAdminMarketsPage,
    getDetailsFieldGroupsLink,
    openDetailsFieldGroupsPage,
    getAdminMarketDetailsFieldsLink,
    openAdminMarketDetailsFieldsPage,
    getAdminMarketPlansLink: getSetupAreaPlansPageLink,
    openAdminSetupAreaPlansPage,
    getAdminMarketPlanLink,
    openAdminMarketPlanPage,
    getAdminSetupAreaVersionPageLink,
    openAdminSetupAreaVersionPage,
    getAdminMarketAiTrainingLink,
    openAdminMarketAiTrainingPage,
    getMarketsLink,
    openMarketsPage,
    getMarketRequestsLink,
    openMarketRequestsPage,
    getMarketDetailBoxesPageLink,
    openMarketDetailBoxesPage,
    openCustomProcessDetailFieldsPage,
    getAdminTasksGroupsManagementLink,
    openAdminTasksGroupsManagementPage,
    getNewClientPortalConfigurationLink,
    openNewClientPortalConfigurationPage,
    getAdminStateMarketsLink,
    openAdminStateMarketsPage,
    getAdminMarketRequestsLink,
    openAdminMarketRequestsPage,
    getAdminMarketRequestLink,
    openAdminMarketRequestPage,
    getAdminSetupAreaPageLink,
    openAdminSetupAreaPage,
    getEmailSetupLink,
    openEmailSetupPage,
    getNewEmailSettingsLink,
    openNewEmailSettingsPage,
  };
}
